import type { PublicCountryConfig } from './types.js';

// ── Web-safe subset of the KG (Kyrgyzstan, manastender.com) config ────────────
// Mirrors `kz.public.ts` field for field. Owner's decisions 2026-11-04: brand
// ManasTender.com, Russian-only interface, som (KGS), Stripe cards + a bank
// invoice request, yearly plans, a Telegram bot whose assistant is Айпери.
//
// Kyrgyz portals publish region names in Russian («Чуйская область», «г. Бишкек»)
// and, on some lots, in Kyrgyz («Чүй облусу», «Бишкек шаары»). The 7 oblasts +
// 2 cities of republican significance live in the DB regions table, seeded per
// deployment (`services/api/prisma/kg-seed.ts`, names from `kg-region.map.ts`).
// The interface is ru-only, so `localize` folds a Kyrgyz name to its Russian twin
// and passes everything else (a district, a free-text address) through unchanged.
const KG_REGION_NAMES: readonly { readonly ru: string; readonly ky: string }[] = [
  { ru: 'г. Бишкек', ky: 'Бишкек шаары' },
  { ru: 'г. Ош', ky: 'Ош шаары' },
  { ru: 'Чуйская область', ky: 'Чүй облусу' },
  { ru: 'Ошская область', ky: 'Ош облусу' },
  { ru: 'Жалал-Абадская область', ky: 'Жалал-Абад облусу' },
  { ru: 'Иссык-Кульская область', ky: 'Ысык-Көл облусу' },
  { ru: 'Нарынская область', ky: 'Нарын облусу' },
  { ru: 'Таласская область', ky: 'Талас облусу' },
  { ru: 'Баткенская область', ky: 'Баткен облусу' },
];

/** Case/space-insensitive key, so «г.Бишкек» and «Г. Бишкек» find their row. */
function regionKey(s: string): string {
  return s.toLowerCase().replace(/\s+/g, '').replace(/ё/g, 'е');
}

const RU_BY_KEY = new Map<string, string>(
  KG_REGION_NAMES.flatMap((r) => [
    [regionKey(r.ru), r.ru],
    [regionKey(r.ky), r.ru],
  ]),
);

export const kgPublic: PublicCountryConfig = {
  code: 'kg',
  // Russian only (owner, 2026-11-04): no Kyrgyz interface. ~6.5 % of the new
  // portal's titles are Kyrgyz; the AI summary renders them in Russian.
  locales: ['ru'],
  defaultLocale: 'ru',
  regions: {
    /**
     * A region name as the ru interface prints it: a Kyrgyz oblast / city name
     * becomes its Russian twin, a Russian one is normalised to the seeded form,
     * anything not in the table is returned unchanged.
     */
    localize(name, _locale) {
      if (!name || !name.trim()) return null;
      return RU_BY_KEY.get(regionKey(name)) ?? name;
    },
  },
  currency: {
    code: 'KGS',
    symbol: 'сом',
    // Same order/shape as uz/kz ru arrays so format.ts picks it by locale key.
    compactUnits: { ru: ['трлн', 'млрд', 'млн', 'тыс.'] },
    words: { ru: 'сом' },
  },
  brand: {
    name: 'ManasTender.com',
    siteUrl: 'https://manastender.com',
    supportEmail: 'support@manastender.com',
    // Owner, 2026-11-04: the same human support handle as KZ; no phone published.
    supportTelegram: { handle: '@flesteyto', url: 'https://t.me/flesteyto' },
    publishPhone: false,
    // Owner, 05.11: a muted crimson that matches the red logo (tile #C8102E,
    // sun #FFD100) without shouting in the interface. `primary` is 6.6:1 on the
    // bone page (#f7f6f3). Every `brand-mid` use is a hover, and teal's hover
    // goes one step LIGHTER (#0a5c5c → #0d7070), so `mid` does the same here
    // (#B53346: 5.5:1 on bone, white on it 6:1) rather than darkening.
    palette: { primary: '#A3283A', mid: '#B53346', light: '#F8E9EB' },
  },
  countryName: {
    ru: { nom: 'Кыргызстан', gen: 'Кыргызстана', prep: 'Кыргызстане' },
  },
  // Web ИНН form: a Kyrgyz ИНН is 14 digits (server keeps the shape validate).
  companyIdForm: { label: 'ИНН', digits: 14 },
  phonePlaceholder: '+996 555 123 456',
  // ОКГЗ (the Kyrgyz procurement classifier) is CPV 2008-shaped: an 8-digit code
  // whose hierarchy is carried by the leading digits (division `45`, group `452`,
  // class `4521`). The portals print a check digit after a hyphen (`45210000-2`);
  // `tenders.ikpu` stores the code WITHOUT it (`45210000` — the KG adapters drop
  // it, the full form stays in `lotDetails`), and the portals' tree
  // (`okgz/findAll`) names nodes by their bare prefix (`0311`).
  // Accepted: the stored 8-digit form and any 2–8 digit prefix, plus the dashed
  // form a user may paste from a portal (the API's results sanitizer folds it to
  // the stored form). Refused: anything else (dots, letters, a 1- or 9-digit run).
  productCodePattern: /^(?:\d{8}-\d|\d{2,8})$/,
  // Owner, 2026-11-04: ManasTender's own Meta Pixel. GA4 off ('' = no gtag).
  analytics: { metaPixelId: '1047979894622600', ga4MeasurementId: '' },
  // Own bot @manastender_bot (assistant Айпери), no channel. A search saved before
  // any chat is linked still gets its first lot by e-mail, as on KZ.
  messaging: {
    telegram: true,
    telegramBot: 'manastender_bot',
    telegramChannel: null,
    emailFirstAlert: true,
    singleFirstAlert: true,
  },
  // Owner, 2026-11-04: analytics on from day one («цена победителя по ОКГЗ и
  // региону» is what Pro sells), Business tier sold, corporate exempt from the
  // contact gate (as KZ), no Uzbek /start and /ip landings, ИИ-онбординг for
  // everyone, honest teasers, no supply-request source.
  features: {
    priceIntel: true,
    teamPlans: true,
    corporateContactGate: false,
    conversionLandings: false,
    aiOnboarding: true,
    honestTeasers: true,
    supplyScope: false,
    // MiLicita's fix (persona test 22.10, D7): a refused login / register code
    // empties the six boxes and puts the caret in the first. KZ lesson #16.
    refusedCodeCleared: true,
  },
  // Every ingested institution. No harvest has run yet, so none is known to be
  // empty — drop a chip here (not from `sourceKeys`) once a source shows 0 KG lots,
  // the way KZ dropped AIIB.
  ifiSources: ['worldbank', 'adb', 'ungm', 'undp', 'isdb', 'ebrd', 'aiib'],
  // The three state portals, in chip order (mitwork.kg joins once its adapter
  // takes an origin override — see kg.ts sourceKeys).
  govSources: ['kg-goszakupki', 'kg-zakupki-gov', 'kg-zakupki-okmot'],
  // Owner, 2026-11-04: Stripe Checkout in som, monthly AND yearly, plus «Оплата
  // по счёту» (an invoice REQUEST form whose requisites are the Uzbek operator's,
  // as on UZ). No ИП tier. Cards are accepted by AI CRM Inc through Stripe, as on KZ.
  payments: {
    checkout: 'stripe',
    yearlyPlans: true,
    bankInvoice: true,
    ipPlan: false,
    marks: ['visa', 'mastercard', 'stripe'],
    cardPayee: {
      name: 'AI CRM Inc',
      address: '251 Little Falls Drive, Wilmington, New Castle County, DE 19808, USA',
      phone: '302-636-5401',
      processor: 'Stripe',
    },
    // Coordinator, 2026-11-04: the invoice is for the YEARLY plans only and is
    // priced in US dollars (SWIFT from Kyrgyzstan to the Uzbek operator); the som
    // price is shown beside it «справочно». Amount = round(som price / rate):
    // 17 940 / 87.45 = 205.15 → $205, 35 940 / 87.45 = 410.98 → $411.
    // ⚠️ Re-derive both when a price or the rate changes.
    invoiceUsd: {
      kgsPerUsd: 87.45,
      rateSource: 'НБКР',
      amountsUsd: { 'pro-yearly': 205, 'business-yearly': 411 },
    },
  },
  timeZone: 'Asia/Bishkek',
};
